import { t } from "@/plugins/vue-i18n";

import { GroupedList, SelectOption } from "@/types/others";
import { CatalogItem } from "@/types/supplier";
import { HIGHLIGHT_COLOR, HIGHLIGHT_MESSAGE } from "@/types/enum/propEnum";
import { CUSTOM_SUPPLIER_TYPE } from "@/types/enum/supplierEnum";
import { APP_LANGUAGES } from "@/types/enum/ionicEnum";
import { FLAG_EMOJI_OFFSET } from "@/assets/variables";
import { INFORMATION_TYPE, NotificationTopic } from "@/types/enum/notification";

const getOptions = <T extends Record<string, unknown>>(originOptions: T[], valueKey: keyof T, textKey: keyof T | ((obj: T) => string | number | undefined)): SelectOption[] => {
    const options: SelectOption[] = []
    for (const item of originOptions) {
        if (!item) continue;
        if (item[valueKey] === undefined) continue;
        if (textKey instanceof Function) {
            const text = textKey(item)
            if (text === undefined) continue;
            options.push({
                value: item[valueKey],
                text
            })
        } else {
            const text = item[textKey]
            if (text === undefined) continue;
            if (!(typeof text === 'string' || typeof text === 'number')) continue;
            options.push({
                value: item[valueKey],
                text
            })
        }
    }
    return options
}

const compareFormat = (value: unknown): string => {
    if (typeof value === 'boolean') return (+value).toString()
    if (typeof value !== 'string' && typeof value !== 'number') return ''
    return value.toString().trim().toLowerCase()
}

const translateJoin = (array: unknown[], translationSelector: string, separator: string): string => {
    const translatedArray: string[] = []

    for (const element of array) {
        translatedArray.push(t(translationSelector + element))
    }

    return translatedArray.join(separator)
}

const getHighlightMessage = (product: CatalogItem): HIGHLIGHT_MESSAGE[] => {
    const messages: HIGHLIGHT_MESSAGE[] = []

    if (!product) return messages

    if (product.isSpecialOffer) messages.push(HIGHLIGHT_MESSAGE.REDUCED)
    if (product.notInStock) messages.push(HIGHLIGHT_MESSAGE.NOT_IN_STOCK)
	if (product.promotions?.length) messages.push(HIGHLIGHT_MESSAGE.PROMOTION)
    return messages
}

export const highlightColor = (highlightMsg: HIGHLIGHT_MESSAGE): HIGHLIGHT_COLOR => {
    if (highlightMsg === HIGHLIGHT_MESSAGE.NEW) return HIGHLIGHT_COLOR.BLUE
    if (highlightMsg === HIGHLIGHT_MESSAGE.REDUCED) return HIGHLIGHT_COLOR.PURPLE
    if (highlightMsg === HIGHLIGHT_MESSAGE.NOT_IN_STOCK) return HIGHLIGHT_COLOR.ORANGE
	if (highlightMsg === HIGHLIGHT_MESSAGE.PROMOTION) return HIGHLIGHT_COLOR.CORAL
    return HIGHLIGHT_COLOR.GREEN
}

const isCustomSupplierEnum = (img: string | CUSTOM_SUPPLIER_TYPE): img is CUSTOM_SUPPLIER_TYPE => {
    return img === CUSTOM_SUPPLIER_TYPE.OTHER
        || img === CUSTOM_SUPPLIER_TYPE.HYGIENE
        || img === CUSTOM_SUPPLIER_TYPE.DRINKS
        || img === CUSTOM_SUPPLIER_TYPE.VEGETABLE
        || img === CUSTOM_SUPPLIER_TYPE.MEAT
}

const toFlagHexCode = (lang: APP_LANGUAGES): string => {
    if (lang === APP_LANGUAGES.EN) return '&#127468&#127463'
    if (lang === APP_LANGUAGES.EL) return '&#127468&#127479'
    return lang.split('').map(value => {
        return `&#${value.charCodeAt(0) + FLAG_EMOJI_OFFSET}`;
    }).join('')
}

const generateBase64Src = (base64: string): string => {
    return `data:image/png;base64, ${base64}`
}

export const convertNumberWithComma = (value: number | string): string => String(value).replace(/\./g, ',')

export const mapNotificationTopic = (kind: NotificationTopic): INFORMATION_TYPE => {
    switch (kind) {
        case NotificationTopic.NEWS:
            return INFORMATION_TYPE.NEWS
        case NotificationTopic.PROMOTION:
            return INFORMATION_TYPE.PROMOTION
        case NotificationTopic.PRODUCT_PROMOTION:
            return INFORMATION_TYPE.PRODUCT_PRESENTATION
        default:
            throw Error(`Type is not an information type: ${kind}`)
    }
}

export const groupRecycleArray = <T extends Record<any, any> & { id: string }>(items: T[], itemsPerRow: number): GroupedList<T>[] => {
    if (items.length === 0) return []
    const groupedList: GroupedList<T>[] = []
    let counter = 0
    let groupedItem: GroupedList<T> = { id: items[0].id }

    for (const item of items) {
        if (counter + 1 > itemsPerRow) {
            groupedList.push(JSON.parse(JSON.stringify(groupedItem)))
            groupedItem = { id: item.id }
            counter = 0
        }
        groupedItem = { ...groupedItem, [counter]: item }
        counter++
    }

    if (groupedItem[0]) groupedList.push(JSON.parse(JSON.stringify(groupedItem)))

    return groupedList
}

export const cleanBody = <T extends Record<any, any>>(body: T, keys: Array<keyof T>) => {
    const cleanedBody = { ...body }
    let key: keyof T;
    for (key in body) {
        if (keys.includes(key)) delete cleanedBody[key]
    }
    return cleanedBody
}


export {
    getOptions,
    compareFormat,
    translateJoin,
    getHighlightMessage,
    isCustomSupplierEnum,
    toFlagHexCode,
    generateBase64Src
}
